import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { getToolLink } from '@/shared/config/tools';
import PageLoader from '@/shared/ui/PageLoader';

const FALLBACK_REVEAL_MS = 1800;

export default function ToolEmbedPage() {
  const { toolId } = useParams();
  const tool = getToolLink(toolId);
  const src = useMemo(() => {
    if (!tool) {
      return '';
    }
    const sep = tool.href.includes('?') ? '&' : '?';
    return `${tool.href}${sep}_cb=${Date.now()}`;
  }, [tool]);

  const [ready, setReady] = useState(false);
  const fallbackTimer = useRef<number | null>(null);

  useEffect(() => {
    setReady(false);

    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'tool:ready') {
        setReady(true);
      }
    };
    window.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('message', onMessage);
      if (fallbackTimer.current) {
        window.clearTimeout(fallbackTimer.current);
        fallbackTimer.current = null;
      }
    };
  }, [src]);

  const handleIframeLoad = () => {
    if (fallbackTimer.current) {
      window.clearTimeout(fallbackTimer.current);
    }
    fallbackTimer.current = window.setTimeout(
      () => setReady(true),
      FALLBACK_REVEAL_MS
    );
  };

  if (!tool) {
    return (
      <div className="rounded-[14px] bg-white p-6 text-foreground">
        <h1 className="mb-2 text-xl font-semibold">Инструмент не найден</h1>
        <Link className="text-primary underline" to="/">
          На главную
        </Link>
      </div>
    );
  }

  return (
    <section
      className="
        fixed inset-0 z-30 overflow-hidden bg-white
        lg:relative lg:inset-auto lg:z-auto lg:-my-2 lg:rounded-[14px]
      "
    >
      <iframe
        title={tool.label}
        src={src}
        onLoad={handleIframeLoad}
        className={`h-[100dvh] w-full border-0 transition-opacity duration-200 lg:h-[calc(100vh-2rem)] ${
          ready ? 'opacity-100' : 'opacity-0'
        }`}
      />
      {!ready ? (
        <div className="absolute inset-0 grid place-items-center bg-white">
          <PageLoader />
        </div>
      ) : null}
    </section>
  );
}
